import { Plus, X } from 'lucide-react';
import { useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode, type WheelEvent } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { resolveDropIndex, resolvePointerDrop, resolveRevealScroll, TAB_DRAG_THRESHOLD_PX, type DropSide } from './tabOrder.js';

export interface WorkspaceTab {
  id: string;
  label: string;
  icon?: LucideIcon;
  closeable?: boolean;
  /** Unsaved changes marker. */
  dirty?: boolean;
  /** Mono hint after the label (account slug). */
  hint?: string;
}

export interface WorkspaceTabsProps {
  tabs: WorkspaceTab[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onClose?: (id: string) => void;
  onAdd?: () => void;
  /**
   * Enables reordering: pointer drag of a tab along the strip (an insertion bar shows the drop
   * side; a press that moves less than 4px is a plain click) and Shift+ArrowLeft/Right on the
   * focused tab. `toIndex` follows move(from, to) semantics (remove `from`, then insert at `to`).
   */
  onReorder?: (fromIndex: number, toIndex: number) => void;
  /** Right slot, e.g. `AS OF 2026-05-19 07:14Z`. */
  rightSlot?: ReactNode;
  'aria-label'?: string;
  className?: string;
}

interface PressState {
  index: number;
  pointerId: number;
  startX: number;
  dragging: boolean;
}

/**
 * Tab strip (DESIGN_SYSTEM section 7.3): active bar inset-x-3 h-[2px], closeable tabs, `+`, reorder.
 * The tabs scroll sideways when they outgrow the strip; `+` is docked outside the scroller so it is
 * always reachable, and the active tab is scrolled into view when it changes.
 */
export function WorkspaceTabs({ tabs, activeId, onSelect, onClose, onAdd, onReorder, rightSlot, className, ...aria }: WorkspaceTabsProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const press = useRef<PressState | null>(null);
  // Set when a pointer-up completed a drag so the click that follows does not also select.
  const swallowClick = useRef(false);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<{ index: number; side: DropSide } | null>(null);

  const tabElement = (id: string) => listRef.current?.querySelector<HTMLElement>(`[data-tab-id="${id}"]`) ?? null;
  const focusTab = (id: string) => tabElement(id)?.focus();

  // Keep the active tab in view: a tab opened at the end of an overflowing strip would otherwise
  // land out of sight. Moves the strip's own scroll offset only (never the page), by the smallest
  // amount that reveals the tab, before paint so the strip never flashes at the old offset.
  useLayoutEffect(() => {
    const list = listRef.current;
    const el = activeId === null ? null : tabElement(activeId);
    if (!list || !el) return;
    const next = resolveRevealScroll(list.getBoundingClientRect(), el.getBoundingClientRect(), list.scrollLeft);
    if (next !== list.scrollLeft) list.scrollLeft = next;
  }, [activeId, tabs]);

  // A mouse wheel has no horizontal axis: vertical wheel movement over the strip scrolls it sideways.
  const onWheel = (e: WheelEvent<HTMLDivElement>) => {
    const list = e.currentTarget;
    if (list.scrollWidth <= list.clientWidth || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
    list.scrollLeft += e.deltaY;
  };

  const tabRects = () => {
    const nodes = listRef.current?.querySelectorAll<HTMLElement>('[data-tab-index]') ?? [];
    return Array.from(nodes)
      .sort((a, b) => Number(a.dataset.tabIndex) - Number(b.dataset.tabIndex))
      .map((n) => {
        const r = n.getBoundingClientRect();
        return { left: r.left, right: r.right };
      });
  };

  const endDrag = () => {
    press.current = null;
    setDragFrom(null);
    setDropTarget(null);
  };

  const dragHandlers = (index: number) =>
    onReorder
      ? {
          onPointerDown: (e: PointerEvent<HTMLDivElement>) => {
            if (e.button !== 0 || press.current) return;
            // The close button is not a drag handle.
            if ((e.target as HTMLElement).closest('[data-tab-close]')) return;
            press.current = { index, pointerId: e.pointerId, startX: e.clientX, dragging: false };
          },
          onPointerMove: (e: PointerEvent<HTMLDivElement>) => {
            const p = press.current;
            if (!p || p.pointerId !== e.pointerId) return;
            if (!p.dragging) {
              if (Math.abs(e.clientX - p.startX) < TAB_DRAG_THRESHOLD_PX) return;
              p.dragging = true;
              e.currentTarget.setPointerCapture(e.pointerId);
              setDragFrom(p.index);
            }
            const next = resolvePointerDrop(tabRects(), e.clientX);
            if (!next) return;
            if (dropTarget?.index !== next.index || dropTarget.side !== next.side) setDropTarget(next);
          },
          onPointerUp: (e: PointerEvent<HTMLDivElement>) => {
            const p = press.current;
            if (!p || p.pointerId !== e.pointerId) return;
            if (p.dragging) {
              const drop = resolvePointerDrop(tabRects(), e.clientX);
              if (drop) {
                const to = resolveDropIndex(p.index, drop.index, drop.side);
                if (to !== p.index) onReorder(p.index, to);
              }
              // The click that follows a captured pointer-up (if any) must not re-select.
              swallowClick.current = true;
              setTimeout(() => {
                swallowClick.current = false;
              }, 0);
              const moved = tabs[p.index];
              if (moved) requestAnimationFrame(() => focusTab(moved.id));
            }
            endDrag();
          },
          onPointerCancel: endDrag,
          onLostPointerCapture: () => {
            if (press.current?.dragging) endDrag();
          },
        }
      : {};

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const count = tabs.length;
    if (count === 0) return;
    if (onReorder && e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      const to = e.key === 'ArrowRight' ? index + 1 : index - 1;
      if (to < 0 || to >= count) return;
      e.preventDefault();
      onReorder(index, to);
      const t = tabs[index];
      if (t) requestAnimationFrame(() => focusTab(t.id));
      return;
    }
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (index + 1) % count;
    else if (e.key === 'ArrowLeft') next = (index - 1 + count) % count;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = count - 1;
    else if ((e.key === 'Delete' || e.key === 'Backspace') && onClose && tabs[index]?.closeable !== false) {
      e.preventDefault();
      const t = tabs[index];
      if (t) onClose(t.id);
      return;
    }
    if (next === null) return;
    e.preventDefault();
    const t = tabs[next];
    if (!t) return;
    onSelect(t.id);
    focusTab(t.id);
  };

  const dragging = dragFrom !== null;

  return (
    <div className={cn('flex h-10 shrink-0 items-stretch justify-between gap-4 border-b border-line bg-bg pl-4 pr-5', className)}>
      <div className="flex min-w-0 items-stretch">
        <div
          ref={listRef}
          role="tablist"
          aria-label={aria['aria-label'] ?? 'Workspace tabs'}
          onWheel={onWheel}
          className={cn('scrollbar-thin flex min-w-0 items-stretch overflow-x-auto', dragging ? 'cursor-grabbing select-none' : '')}
        >
          {tabs.map((tab, i) => {
            const active = tab.id === activeId;
            const Icon = tab.icon;
            const drop = dropTarget?.index === i ? dropTarget.side : null;
            return (
              <div
                key={tab.id}
                data-tab-index={i}
                data-drop-side={drop ?? undefined}
                className={cn('group relative flex shrink-0 items-stretch', onReorder ? 'touch-none' : '', dragFrom === i ? 'opacity-50' : '')}
                {...dragHandlers(i)}
              >
                {drop ? <span aria-hidden className={cn('pointer-events-none absolute inset-y-2 z-10 w-[2px] bg-brand', drop === 'before' ? 'left-0' : 'right-0')} /> : null}
                <button
                  type="button"
                  role="tab"
                  data-tab-id={tab.id}
                  aria-selected={active}
                  tabIndex={active ? 0 : -1}
                  onClick={() => {
                    if (swallowClick.current) {
                      swallowClick.current = false;
                      return;
                    }
                    onSelect(tab.id);
                  }}
                  onKeyDown={(e) => onKeyDown(e, i)}
                  className={cn(
                    'relative flex items-center gap-2 px-4 py-2.5 text-nav transition-colors focus-visible:outline-offset-[-1px]',
                    active ? 'text-ink' : 'text-mute hover:text-ink',
                    tab.closeable !== false && onClose ? 'pr-7' : '',
                  )}
                >
                  {Icon ? <Icon size={12} strokeWidth={1.5} aria-hidden className="shrink-0" /> : null}
                  <span className="truncate">{tab.label}</span>
                  {tab.hint ? <span className="font-mono text-2xs uppercase tracking-[0.15em] text-mute">{tab.hint}</span> : null}
                  {tab.dirty ? <span aria-label="Unsaved changes" role="img" className="h-1.5 w-1.5 rounded-full bg-warn" /> : null}
                  {active ? <span aria-hidden className="absolute inset-x-3 bottom-0 h-[2px] bg-brand" /> : null}
                </button>
                {tab.closeable !== false && onClose ? (
                  <button
                    type="button"
                    data-tab-close
                    aria-label={`Close ${tab.label}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose(tab.id);
                    }}
                    className={cn(
                      'absolute right-1.5 top-1/2 inline-flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-sm text-mute transition-colors hover:bg-card hover:text-ink',
                      active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 group-focus-within:opacity-100',
                    )}
                  >
                    <X size={10} aria-hidden />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
        {/* Docked outside the scroller: reachable however far the strip is scrolled. */}
        {onAdd ? (
          <button type="button" onClick={onAdd} aria-label="New tab" className="inline-flex shrink-0 items-center px-3 text-mute transition-colors hover:text-ink">
            <Plus size={12} aria-hidden />
          </button>
        ) : null}
      </div>
      {rightSlot ? <div className="flex shrink-0 items-center gap-2 font-mono text-2xs uppercase tracking-[0.18em] text-mute">{rightSlot}</div> : null}
    </div>
  );
}

/** `AS OF 2026-05-19 07:14Z` right slot. */
export function AsOf({ value, label = 'As of' }: { value: string; label?: string }) {
  return (
    <>
      <span>{label}</span>
      <span className="normal-case tracking-normal text-ink">{value}</span>
    </>
  );
}
